<template>
  <div>
    <modal-handler />
    <router-view />
    <b-alert
      v-if="offlineQueueCount > 0"
      class="position-fixed m-3"
      style="z-index: 2000; right: 0; bottom: 0; cursor: pointer;"
      variant="warning"
      show
      @click.native="openOfflineQueueModal()"
    >
      <i class="fa fa-cloud-upload-alt mr-2"></i>
      {{ $t("offlinePending", { n: offlineQueueCount }) }}
      <small class="d-block text-muted">{{ $t("offlinePendingDetails") }}</small>
    </b-alert>
    <b-modal
      id="offline-queue-modal"
      v-model="showOfflineQueueModal"
      :title="$t('offlineQueueTitle')"
      centered
      :hide-footer="true"
    >
      <div v-if="offlineQueueItems.length === 0">
        {{ $t("offlineQueueEmpty") }}
      </div>
      <b-list-group v-else>
        <b-list-group-item v-for="item in offlineQueueItems" :key="item.key">
          <div class="d-flex justify-content-between">
            <strong>{{ (item.method || 'post').toUpperCase() }}</strong>
            <small class="text-muted">{{ formatQueueTime(item.queuedAt) }}</small>
          </div>
          <div class="text-muted">{{ item.url }}</div>
        </b-list-group-item>
      </b-list-group>
    </b-modal>
    <b-alert
      v-model="newVersionAvailable"
      class="position-fixed fixed-top m-0 rounded-0"
      style="z-index: 2000;"
      variant="success"
      dismissible
      :dismissed="versionCheckDismissed"
    >
      <span class="mr-3">{{ $t("newVersionAvailable") }}</span>
      <b-button @click="reload" class="mr-3">{{$t("update")}}</b-button>
      <b-button @click="versionCheckDismissed">{{$t("cancel")}}</b-button>
    </b-alert>
  </div>
</template>
<script>
import ModalHandler from "@/components/ModalHandler";
import AzureAuthService from "@/services/AzureAuthService";
import OfflineQueueService from "@/services/OfflineQueueService";
import { mapActions, mapGetters } from "vuex";
import CompanyService from "@/services/CompanyService";
import {version} from '@/../package.json'
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { Capacitor } from "@capacitor/core";
import * as Sentry from "@sentry/vue";

export default {
  components: {
    ModalHandler
  },
  data() {
    return {
      newVersionAvailable: false,
      offlineQueueItems: [],
      showOfflineQueueModal: false
    }
  },
  mounted() {
    // Set favicon

    // Notify live update that app is ready
    if(Capacitor.isNativePlatform()){
      LiveUpdate.ready();
    }

    if( process.env.VUE_APP_ENVIRONMENT == "prod" || Capacitor.isNativePlatform() ){
      this.checkVersion();

      this.interval = setInterval(() => {
        this.checkVersion()
      }, 10 * 60 * 1000);
    }

    var hostname = document.location.hostname;
    var partnerRef = null;

    if (hostname == "www.app.keeada.eu" || hostname == "app.keeada.eu") {
      partnerRef = "keeada";
    } else if (hostname == "www.app.isocertifiering.nu" || hostname == "app.isocertifiering.nu") {
      partnerRef = "isocertifiering";
    } else if (hostname == "www.app.qbic.se" || hostname == "app.qbic.se") {
      partnerRef = "qbic";
    }else if (hostname == "www.app.veroenable.com" || hostname == "app.veroenable.com"){
      partnerRef = "veroenable";
    }

    if (partnerRef && !this.$partner) {
      this.$setIsLoadingPartner(true);
      CompanyService.getPartnerByPartnerRef(partnerRef)
        .then((company) => {
          this.$setPartner(company);
          if (company.favicon != null) {
            const favicon = document.getElementById("favicon");
            favicon.href = company.favicon.filepath;
          }

          if (company.favicon != null) {
            window.document.title = partnerRef;
          }
        })
        .finally(() => {
          this.$setIsLoadingPartner(false);
        });
    }
  },
  computed: {
    ...mapGetters("app", ["offlineQueueCount"]),
    isIdle() {
      return this.$store.state.idleVue.isIdle;
    }
  },
  methods: {
    ...mapActions("auth", ["logout"]),
    ...mapActions("layout", {
      $setPartner: "setPartner",
      $setIsLoadingPartner: "setIsLoadingPartner"
    }),
    openOfflineQueueModal() {
      this.offlineQueueItems = OfflineQueueService.readQueue();
      this.showOfflineQueueModal = true;
    },
    formatQueueTime(timestamp) {
      if (!timestamp) {
        return "";
      }
      return new Date(timestamp).toLocaleString();
    },
    parseMajorMinor(version) {
      const match = version.match(/(\d+)\.(\d+)/)
      if (!match) return null
      return `${match[1]}.${match[2]}`
    },
    async checkVersion() {
      try {
        const response = await fetch(
          'https://liveupdates.prewoe.com/production/manifest.json',
          { cache: 'no-store' }
        );
        const manifest = await response.json();

        const newVersion = manifest.version;
        const newMajorMinor = this.parseMajorMinor(newVersion);
        let currentVersion;
        let currentMajorMinor;

        if (Capacitor.isNativePlatform()) {
          const currentBundle = await LiveUpdate.getCurrentBundle();

          if (currentBundle.bundleId) {
            currentVersion = currentBundle.bundleId;
          } else {
            // fallback till web-appens version
            currentVersion = version;
          }

          currentMajorMinor = this.parseMajorMinor(currentVersion);

          // Patch diff → alltid auto-update
          if (newVersion !== currentVersion) {
            // Bundlen kan redan finnas från en tidigare check (t.ex. innan omladdning)
            const { bundleIds } = await LiveUpdate.getBundles();
            if (!bundleIds.includes(newVersion)) {
              try {
                await LiveUpdate.downloadBundle({
                  url: manifest.url,
                  bundleId: newVersion,
                });
              } catch (err) {
                // En parallell nedladdning (t.ex. startad innan omladdning) kan redan ha lagt till bundlen,
                // då kastar iOS "couldn't be moved ... because an item with the same name already exists"
                const { bundleIds: bundleIdsAfterError } = await LiveUpdate.getBundles();
                if (!bundleIdsAfterError.includes(newVersion)) {
                  throw err;
                }
              }
            }

            await LiveUpdate.setNextBundle({ bundleId: newVersion });

            // Check major/minor diff → visa notifiering först när bundlen är redo
            if (newMajorMinor !== currentMajorMinor) {
              this.newVersionAvailable = true;
            }
          }

        }
        else {
          // Web-case
          currentMajorMinor = this.parseMajorMinor(version);

          if (newMajorMinor !== currentMajorMinor) {
            this.newVersionAvailable = true;
          }
        }
      } catch (err) {
        console.error("Failed to fetch new version", err);
        Sentry.captureException(err);
      }
    },
    async reload() {
      if (Capacitor.isNativePlatform()) {
        try {
          await LiveUpdate.reload()
        }
        catch (err) {
          console.error('Could not reload the app:', err)
          Sentry.captureException(err);
        }
      }
      else{
        window.location.reload(true)
      }
    },
    versionCheckDismissed(){
      this.newVersionAvailable = false;
      clearInterval(this.interval);
    }
  },
  created() {
  }
};
</script>
